import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { Button } from '../../components/button/Button';
import { PopoverButton } from '../../components/button/PopoverButton';
import { FlyoutFooter } from '../../components/flyout-components/FlyoutFooter';
import { StatusCode } from '../../utils/api';
import { MetricsSearchFilters } from '../models/metrics-search-filters';
import { UnplannedPickupMetricsGroup } from '../models/metrics/unplanned-pickup-metrics-group';
import { UnplannedPickupOrder } from '../models/metrics/unplanned-pickup-order';
import { ProductionSvc } from '../service';

interface IProps {
    isOpen: boolean;
    /** Called when the flyout is closed, and whether the groups were saved so that the list has to be fetched again */
    onClose: (saved: boolean) => void;
    /** The group that was selected: the flyout is for all of the orders of its dealer or address */
    group: UnplannedPickupMetricsGroup | undefined;
    /** The search the group was found by */
    filters: MetricsSearchFilters | undefined;
}

// The group of an order that was never split from the others of its dealer or address
const UNGROUPED = 0;

const sortOrders = (list : UnplannedPickupOrder[]) =>
    [...list].sort((a, b) => a.groupId - b.groupId || a.orderId.localeCompare(b.orderId));

/**
 * Splits the orders of a dealer's pickups, or of the deliveries to an address, into groups, as is done for routing, so
 * that a production run does not have to be made from all of them.  Each group is a section of its own: select orders by
 * clicking or dragging over them, and make a group of them or move them to a group that is there, or put them all back
 * in one.  Nothing is done to the orders until the changes are saved, so none of it asks first.
 */
export const OrderGroupingFlyout = (props : IProps) => {

    const [service] = React.useState(new ProductionSvc());
    const [orders, setOrders] = React.useState<UnplannedPickupOrder[]>([]);
    const [original, setOriginal] = React.useState<{ [orderId: string]: number }>({});
    const [checked, setChecked] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);

    // Drag to select: the row the mouse went down on, the row it is over, and whether the rows between get selected or
    // let go, which is the opposite of what the first row was.  Rows are told apart by where they are in the orders,
    // which are in the order of their groups, so a drag can go from one group to the next.
    const [drag, setDrag] = React.useState<{ start: number, current: number, add: boolean } | undefined>(undefined);

    /**
     * Fetch the orders of the dealer or the address, of every group, when the flyout opens
     */
    React.useEffect(() => {
        if (props.isOpen && props.group) {
            setChecked([]);
            setOrders([]);

            const load = async () => {
                const result = await service.getUnplannedPickupOrders(props.filters, props.group!, false, false);
                if (result.status === StatusCode.Success) {
                    const sorted = sortOrders(result.data);

                    setOrders(sorted);
                    setOriginal(sorted.reduce((accum : { [orderId: string]: number }, o : UnplannedPickupOrder) => {
                        accum[o.orderId] = o.groupId;
                        return accum;
                    }, {}));
                }
            };

            load();
        }
    }, [props.isOpen]);

    // The groups are told apart by their number in the order they are in: 1, 2, ...  A group with no orders is not there.
    const groupIds = Array.from(new Set(orders.map((o : UnplannedPickupOrder) => o.groupId))).sort((a, b) => a - b);

    const hasChanges = orders.some((o : UnplannedPickupOrder) => o.groupId !== original[o.orderId]);

    const toggle = (orderId : string) => {
        setChecked(checked.includes(orderId)
            ? checked.filter((id : string) => id !== orderId)
            : [...checked, orderId]);
    };

    const isInDrag = (index : number) =>
        drag !== undefined && index >= Math.min(drag.start, drag.current) && index <= Math.max(drag.start, drag.current);

    // What a row is while the mouse is dragged over it: what the drag is doing to the rows it covers
    const isSelected = (index : number, orderId : string) => isInDrag(index) ? drag!.add : checked.includes(orderId);

    const startDrag = (index : number, e : React.MouseEvent) => {
        if (e.button !== 0) {
            return;
        }

        // keeps the text from being selected as the mouse is dragged
        e.preventDefault();
        setDrag({ start: index, current: index, add: !checked.includes(orders[index].orderId) });
    };

    const dragOver = (index : number) => {
        if (drag !== undefined && drag.current !== index) {
            setDrag({ ...drag, current: index });
        }
    };

    /**
     * Let go of the mouse anywhere: select, or let go of, the rows that were dragged over
     */
    React.useEffect(() => {
        if (drag === undefined) {
            return undefined;
        }

        const finish = () => {
            const dragged = orders
                .slice(Math.min(drag.start, drag.current), Math.max(drag.start, drag.current) + 1)
                .map((o : UnplannedPickupOrder) => o.orderId);

            setChecked(drag.add
                ? Array.from(new Set([...checked, ...dragged]))
                : checked.filter((id : string) => !dragged.includes(id)));
            setDrag(undefined);
        };

        window.addEventListener('mouseup', finish);
        return () => window.removeEventListener('mouseup', finish);
    }, [drag]);

    const moveChecked = (groupId : number) => {
        setOrders(sortOrders(orders.map((o : UnplannedPickupOrder) =>
            Object.assign(new UnplannedPickupOrder(), o, checked.includes(o.orderId) ? { groupId } : {}))));
        setChecked([]);
    };

    /**
     * Take the selected orders out of their groups and make a group of their own of them
     */
    const makeGroup = () => {
        moveChecked(orders.reduce((max : number, o : UnplannedPickupOrder) => Math.max(max, o.groupId), 0) + 1);
    };

    /**
     * Move the selected orders to a group that is there.  A group that is left with no orders is gone.
     * @param groupId The group to move them to
     */
    const moveToGroup = (groupId? : number) => {
        if (groupId !== undefined) {
            moveChecked(groupId);
        }
    };

    /**
     * Take every order out of its group and put them all back in one, the way orders are before they are split
     */
    const resetGroups = () => {
        setOrders(sortOrders(orders.map((o : UnplannedPickupOrder) =>
            Object.assign(new UnplannedPickupOrder(), o, { groupId: UNGROUPED }))));
        setChecked([]);
    };

    const save = async () => {
        setIsSaving(true);

        const moved = orders.filter((o : UnplannedPickupOrder) => o.groupId !== original[o.orderId]);
        const result = await service.updateOrderGroups(moved);

        setIsSaving(false);

        if (result.status === StatusCode.Success) {
            props.onClose(true);
        }
    };

    return (
        <Drawer
            anchor="right"
            onClose={ () => props.onClose(false) }
            open={ props.isOpen }>
            <div className="stop-action-drawer stop-action-reorder-route production-module-flyout">
                <div className="row">
                    <div className="col">
                        <h1>Set Grouping</h1>
                        <h3>{ props.group?.dealerName }{ props.group?.shipToAddressId ? ` - ${ props.group.name }` : '' }</h3>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <p>
                            Select the orders by clicking them or dragging over them, make a group of them with 'New Group' or move
                            them to a group with 'Move to Group', and 'Reset' puts them all back in one group.  A production run can
                            then be made from a group without the rest of the orders.
                        </p>
                    </div>
                </div>

                <div className="row">
                    <div className="col order-grouping-actions order-grouping-toolbar">
                        <Button
                            text="New Group"
                            icon="la-magic"
                            classes="action"
                            testID="make-order-group"
                            disable={ checked.length === 0 }
                            onBtnClick={ makeGroup } />
                        <PopoverButton
                            label="Move to Group"
                            testID="move-order-group"
                            isDisabled={ !(groupIds.length > 1 && checked.length > 0) }
                            onItemSelected={ moveToGroup }
                            listItems={ groupIds.map((id : number, i : number) => ({ id, display: `Add to Group #${ i + 1 }` })) } />
                        <Button
                            text="Reset"
                            icon="la-undo"
                            classes="negative"
                            testID="reset-order-groups"
                            disable={ orders.every((o : UnplannedPickupOrder) => o.groupId === UNGROUPED) }
                            onBtnClick={ resetGroups } />
                    </div>
                </div>

                {
                    groupIds.map((groupId : number, sectionIndex : number) => {
                        const inGroup = orders.filter((o : UnplannedPickupOrder) => o.groupId === groupId);

                        return (
                            <div className="row order-grouping-section" key={ groupId }>
                                <div className="col">
                                    <div className="order-grouping-section-header">
                                        <h3>{ `Order Grouping #${ sectionIndex + 1 }` }</h3>
                                        <span>{ inGroup.length === 1 ? '1 order' : `${ inGroup.length } orders` }</span>
                                    </div>
                                    <table className="table table-hover order-grouping-table">
                                        <thead>
                                            <tr>
                                                <th />
                                                <th>Order ID</th>
                                                <th>Description</th>
                                                <th>Job Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                inGroup.map((itm : UnplannedPickupOrder) => {
                                                    const index = orders.indexOf(itm);

                                                    return (
                                                        <tr
                                                            key={ itm.orderId }
                                                            className={ isSelected(index, itm.orderId) ? 'is-selected' : '' }
                                                            onMouseDown={ (e) => startDrag(index, e) }
                                                            onMouseEnter={ () => dragOver(index) }>
                                                            <td>
                                                                { /* the row takes the mouse, so a click or a drag on the box selects too; the box is for the keyboard */ }
                                                                <input
                                                                    type="checkbox"
                                                                    className="order-grouping-checkbox"
                                                                    aria-label={ `Select ${ itm.orderId }` }
                                                                    checked={ isSelected(index, itm.orderId) }
                                                                    onChange={ () => toggle(itm.orderId) } />
                                                            </td>
                                                            <td>{ itm.orderId }</td>
                                                            <td>{ itm.description }</td>
                                                            <td>{ itm.jobName }</td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })
                }

                { /* room for the last orders to be above the footer */ }
                <div className="order-grouping-end" />

                <FlyoutFooter>
                    <div className="row">
                        <div className="col order-grouping-actions">
                            <Button
                                text="Cancel"
                                classes="action"
                                testID="cancel-order-grouping"
                                onBtnClick={ () => props.onClose(false) } />
                            <Button
                                text="Save"
                                classes="attention"
                                testID="save-order-grouping"
                                disable={ !hasChanges || isSaving }
                                onBtnClick={ save } />
                        </div>
                    </div>
                </FlyoutFooter>
            </div>
        </Drawer>
    );
};
